import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import PropTypes from 'prop-types';

import { useVpSnackbar } from '../../hooks';
import {
  useUpdateMediaDetails,
  useCreateThumbnailPresignedUrl,
  useUploadToPresignedUrl
} from '../../../hooks/react-query';
import { VpDialogTitle } from '../../components/VpDialogTitle';
import { ImageElement } from '../../../components/CampaignEdit/ImageElement';
import { extractThumbnailPath } from '../../../utils/extractThumbnailPath';
import { getFullMediaUrlFromPresignedUrl } from '../../../utils/getFullMediaUrlFromPresignedUrl';
import { initialState, actions, reducer } from './VpUpdateThumbnailModal.state';
import { VpDialogContent } from '../../components';

const {
  CLOSE_IMAGE_EDITOR,
  START_PRESIGNED_URL,
  PRESIGNED_URL_SUCCESS,
  START_THUMBNAIL_UPLOAD,
  THUMBNAIL_UPLOAD_SUCCESS,
  FILE_SELECTED,
  LOAD_EXISTING_IMAGE,
  CLEAR_IMAGE_EDITOR,
  IMAGE_EDITOR_CHANGED,
  FINISHED_LOADING
} = actions;

const buildNewThumbnailUrlFromPresignedUrl = (presignedUrl) => {
  const urlWithoutQueryParams = getFullMediaUrlFromPresignedUrl(presignedUrl);
  return extractThumbnailPath(urlWithoutQueryParams);
};

const VpUpdateThumbnailModal = ({
  selectedMedia,
  updatingThumbnail = '',
  setUpdatingThumbnail = () => {}
}) => {
  const { mutate: updateMediaMutation, isSuccess: isUpdateSuccess } = useUpdateMediaDetails();
  const {
    mutate: createPresignedUrlMutation,
    isSuccess: createPresignedUrlSuccess,
    isError: isPresignedUrlError,
    data: presignedUrlData
  } = useCreateThumbnailPresignedUrl();
  const {
    mutate: uploadThumbnailImageMutation,
    isSuccess: isUploadSuccess,
    isError: isUploadError
  } = useUploadToPresignedUrl();
  const { addSnackbarSuccess, addSnackbarError, addSnackbarInfo } = useVpSnackbar();

  const [state, dispatch] = React.useReducer(reducer, initialState);
  const {
    presignedUrl,
    thumbnailFile,
    newThumbnailUrl,
    isUploading,
    hasEditorChanged,
    finishedLoading
  } = state;

  const isVisible = !!updatingThumbnail;

  useEffect(() => {
    if (isPresignedUrlError || isUploadError) {
      addSnackbarError({
        message: 'There was an issue updating your thumbnail.'
      });
    }
  }, [isPresignedUrlError, isUploadError]);

  useEffect(() => {
    if (createPresignedUrlSuccess) {
      const { presignedUrl } = presignedUrlData;
      dispatch({ type: PRESIGNED_URL_SUCCESS, presignedUrl });
    }
  }, [createPresignedUrlSuccess]);

  useEffect(() => {
    if (isUploadSuccess) {
      dispatch({
        type: THUMBNAIL_UPLOAD_SUCCESS
      });

      const mediaMutationPayload = buildUpdateMediaMutationPayload(selectedMedia);
      updateMediaMutation({
        mediaId: selectedMedia.mediaId,
        data: mediaMutationPayload
      });
    }
  }, [isUploadSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      addSnackbarSuccess({
        message: 'Thumbnail was updated.'
      });
      setUpdatingThumbnail(null);
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (thumbnailFile) {
      addSnackbarInfo({
        message:
          'Loaded new image. You can now edit it if you wish, and press "Save Changes" when you are ready.'
      });
    }
  }, [thumbnailFile]);

  useEffect(() => {
    dispatch({ type: CLOSE_IMAGE_EDITOR });
  }, [isVisible]);

  const buildUpdateMediaMutationPayload = (media) => ({
    title: media.mediaTitle || '',
    description: media.mediaDescription || '',
    thumbnailUrl: newThumbnailUrl || media.thumbnailUrl
  });

  return (
    <Dialog fullWidth maxWidth="lg" onClose={() => setUpdatingThumbnail(null)} open={isVisible}>
      <VpDialogTitle leftContent="Upload Thumbnail" onClose={() => setUpdatingThumbnail(null)} />
      <VpDialogContent>
        <React.Suspense>
          <ImageElement
            disableSort
            hideDeleteButton
            hideDeleteButtonMissingImage
            disableMargins
            isCustomSuccessMessage
            isMediaSubmissionsPage
            hasUploadError={isUploadError}
            presignedUrl={presignedUrl}
            mediaUrl={selectedMedia?.thumbnailUrl}
            editMode={true}
            loadedImage={thumbnailFile}
            isLoading={isUploading}
            hasEditorChanged={hasEditorChanged}
            finishedLoading={finishedLoading}
            _getPresignedUrl={() => {
              dispatch({
                type: START_PRESIGNED_URL
              });
              createPresignedUrlMutation();
            }}
            _uploadAssetToBucket={(imageToUpload, presignedUrl) => {
              const newThumbnailUrl = buildNewThumbnailUrlFromPresignedUrl(presignedUrl);
              dispatch({ type: START_THUMBNAIL_UPLOAD, newThumbnailUrl });
              uploadThumbnailImageMutation({ presignedUrl, file: imageToUpload });
            }}
            _editImageElementImage={() => {
              dispatch({ type: CLOSE_IMAGE_EDITOR });
              setUpdatingThumbnail(null);
            }}
            _selectImageElementImage={(idx, file) => {
              dispatch({ type: FILE_SELECTED, thumbnailFile: file });
            }}
            _resetImageEditorState={() => {
              dispatch({ type: CLEAR_IMAGE_EDITOR });
            }}
            _loadImageElementImage={(idx, mediaUrl) => {
              dispatch({ type: LOAD_EXISTING_IMAGE, loadedMediaUrl: mediaUrl });
            }}
            _setIsEditedState={(dix, editedState) => {
              dispatch({ type: IMAGE_EDITOR_CHANGED, editedState });
            }}
            _setFinishedLoadingState={() => {
              dispatch({ type: FINISHED_LOADING });
            }}
          />
        </React.Suspense>
      </VpDialogContent>
    </Dialog>
  );
};

VpUpdateThumbnailModal.propTypes = {
  selectedMedia: PropTypes.shape({
    mediaId: PropTypes.string,
    thumbnailUrl: PropTypes.string
  }),
  updatingThumbnail: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setUpdatingThumbnail: PropTypes.func
};

export default VpUpdateThumbnailModal;
VpUpdateThumbnailModal.displayName = 'VpUpdateThumbnailModal';



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpMediaTable/VpUpdateThumbnailModal.jsx